import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { shallowEqual } from 'react-redux';

import { IAddKycDocumentProps } from '../components/modals/KycDocumentUploadModal';

import { useProfileContext } from '@/features/context/ProfileContext';
import { useDashboardConfig } from '@/hooks/useDashboardConfig';
import { useAppSelector, useAppDispatch } from '@/hooks/redux-helpers';

import { openModal } from '@/store/actions/core.actions';

import { submitKycReview } from '@/features/kyc/store/actions/kyc-profile.actions';

import { selectProfileKYCByUserId, selectProfileKYCDocumentsByUserId } from '@/store/selectors';
import { selfEmployedLegalForms, TLegalFormKey, UserTypes } from '@/features/user/store/user.model';

export function useKycInfos() {
  const { user } = useProfileContext();
  const config = useDashboardConfig();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const allDocuments =
    useAppSelector(
      (state) =>
        selectProfileKYCDocumentsByUserId(state, user.userId)?.state?.map((kycDocument) => kycDocument.document),
      shallowEqual,
    ) || [];

  const isKycDocLoading = useAppSelector((state) => selectProfileKYCByUserId(state, user.userId)?.isLoading);

  const validationDate = useMemo(() => {
    const reviewComment = user?.kycReviewComment;
    if (!reviewComment) return undefined;

    const validatedIndex = reviewComment.lastIndexOf('VALIDATED');

    if (validatedIndex !== -1) {
      const matches = [...reviewComment.matchAll(/\b(\d{2})-(\d{2})-(\d{4})\s+-\s+(\d{2}):(\d{2}):(\d{2})\b/g)];

      let distance = Infinity;
      let closestDate = undefined;
      matches.forEach((match) => {
        if (match.index < validatedIndex && validatedIndex - match.index < distance) {
          distance = validatedIndex - match.index;
          closestDate = match;
        }
      });

      const [, day, month, year, hours, minutes, seconds] = closestDate;
      const date = new Date(year, month - 1, day, hours, minutes, seconds);

      return date?.toString() !== 'Invalid Date' ? date : undefined;
    }
  }, [user?.kycReviewComment]);

  const isKyc =
    user.userTypeId === UserTypes.naturalPerson &&
    !selfEmployedLegalForms.includes(Number(user?.legalForm) as TLegalFormKey);
  const pageTitle = isKyc
    ? t('pages.currentProfiles.profile.tabs.title.kyc')
    : t('pages.currentProfiles.profile.tabs.title.kyb');

  const hasAdditionalVigilanceMeasureBlock = !!config?.settings.users?.kyc?.useAdditionalVigilanceMeasure;

  const isKycActionDisabled = user.kycLevel === 4 && user.kycReview === 3;
  const isUserStatusNotOkToSubmitKyc = user?.userStatus === 'PENDING' && Number(user?.codeStatus) === 110012;
  const hasDocumentToReview = allDocuments.some(
    (document) => document.isAgent && !document.metadata?.treezorDocumentId && document.status === 'PENDING',
  );

  const isSubmitKycLoading = useAppSelector(
    (state) => selectProfileKYCDocumentsByUserId(state, user.userId)?.submit?.isLoading,
  );

  const handleReviewKyc = () => user?.userId && dispatch(submitKycReview(user?.userId));

  const isUploadDocumentLoading = useAppSelector(
    (state) => selectProfileKYCDocumentsByUserId(state, user.userId)?.create?.isLoading,
  );

  const toggleKycDocumentUploadModal = () =>
    dispatch(openModal<IAddKycDocumentProps>('kycDocumentUploadModal', { user, title: pageTitle }, { maxWidth: 'xl' }));

  return {
    user,
    isKyc,
    pageTitle,
    hasAdditionalVigilanceMeasureBlock,
    validationDate,
    isKycActionDisabled,
    isUserStatusNotOkToSubmitKyc,
    isSubmitKycLoading,
    handleReviewKyc,
    isUploadDocumentLoading,
    toggleKycDocumentUploadModal,
    hasDocumentToReview,
    isKycDocLoading,
  };
}
